import { Close as CloseIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { NoteTextOutline, Excel } from '@opengov/react-capital-assets';
import { IStory, IStoryContent } from '@opengov/stories-shared/types';
import React, { useCallback, useRef, useState } from 'react';
import { modals as modalMessages } from '../../../../../locale/stories.en.locale.json';
import { useEmbedSession } from '../../../../../modals/components/AddTile/hooks/useEmbedSession';
import { IUpdateStoryContentProps } from '../../../../../types/storyViewTypes';
import { IModalMessages } from '../../../../../types';
import locale from '../customReports-locale.json';
import { useStoryContents } from '../../../StoryView/hooks';
import { AddCustomExcelReport } from './AddCustomExcelReport';
import { AddOpenGovReport } from './AddOpenGovReport/AddOpenGovReport';
import { styles } from './AddReportTableModal.styles';
import { ReportTableHandle } from './AddReportTableModal.types';
import { assertNotNil, assertUnreachable } from '../../../../../types/typeUtils';
import { useToast } from '../../../Toast/context/useToast.context';

type ReportType = 'opengov-report' | 'custom-excel-report';

type Props = {
  isOpen: boolean;
  onClose(): void;
  entityId: number;
  entitySubdomain: string;
  story: IStory;
  storyContent: IStoryContent;
  mediaIndex: number | null;
  publicationId: string;
  isTemplate: boolean;
  updateStoryContent(storyContent: IStoryContent): void;
  updateStoryContentDB(props: IUpdateStoryContentProps): void;
};

export function AddReportTableModal({
  isOpen,
  onClose,
  entityId,
  entitySubdomain,
  story,
  storyContent,
  mediaIndex,
  publicationId,
  isTemplate,
  updateStoryContent,
  updateStoryContentDB
}: Props) {
  const [reportType, setReportType] = useState<ReportType>('opengov-report');
  const [isFormValid, setIsFormValid] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const customExcelReportRef = useRef<ReportTableHandle | null>(null);
  const openGovReportRef = useRef<ReportTableHandle | null>(null);
  const { toggleToast } = useToast();
  const { entity, user } = useEmbedSession(entitySubdomain);
  const storyContentsQuery = useStoryContents(
    entityId > 0 ? { storyId: story._id, entityId, isTemplate } : null,
    locale.errors.somethingWentWrong
  );
  const storyContents = storyContentsQuery.data ?? [];

  const handleFormDataChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  const handleFormLoadingChange = useCallback((isLoading: boolean) => {
    setIsFormLoading(isLoading);
  }, []);

  const handleAdd = async () => {
    try {
      setIsAdding(true);
      switch (reportType) {
        case 'custom-excel-report': {
          const customExcelReport = assertNotNil(customExcelReportRef.current);
          await customExcelReport.handleSubmit();
          onClose();
          break;
        }
        case 'opengov-report': {
          const openGovReport = assertNotNil(openGovReportRef.current);
          await openGovReport.handleSubmit();
          onClose();
          break;
        }
        default: {
          assertUnreachable(reportType, 'reportType');
        }
      }
    } catch (error) {
      console.error(error);
      toggleToast({
        title: locale.errors.somethingWentWrong,
        body: locale.errors.unableToAddTable,
        severity: 'error'
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleReportTypeChange = (type: ReportType) => {
    setReportType(type);
    // Reset form when switching report types
    setIsFormValid(false);
    setIsFormLoading(false);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth={true}
      data-test="add-report-table-modal"
      disableEscapeKeyDown
    >
      <DialogTitle sx={styles.dialogTitle}>
        {locale.addReportTableModal.title}
        <IconButton onClick={onClose} sx={styles.closeIconButton} data-test="add-report-table-modal-close-button">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent} dividers={true}>
        <Box sx={styles.dialogContentBox}>
          <Box sx={styles.reportTypeSelector}>
            <Card
              sx={[
                styles.reportTypeCard,
                reportType === 'opengov-report' ? styles.reportTypeCardSelected : null,
                isAdding ? styles.reportTypeCardDisabled : null
              ]}
              onClick={isAdding ? undefined : () => handleReportTypeChange('opengov-report')}
              data-test="add-report-table-modal-opengov-report-card"
            >
              <CardContent sx={styles.reportTypeCardContent}>
                <SvgIcon component={NoteTextOutline} inheritViewBox sx={styles.reportTypeIcon} />
                <Typography variant="body1" sx={styles.reportTypeLabel}>
                  {locale.addReportTableModal.opengovReport}
                </Typography>
              </CardContent>
            </Card>
            <Card
              sx={[
                styles.reportTypeCard,
                reportType === 'custom-excel-report' ? styles.reportTypeCardSelected : null,
                isAdding ? styles.reportTypeCardDisabled : null
              ]}
              onClick={isAdding ? undefined : () => handleReportTypeChange('custom-excel-report')}
              data-test="add-report-table-modal-custom-excel-report-card"
            >
              <CardContent sx={styles.reportTypeCardContent}>
                <SvgIcon component={Excel} inheritViewBox sx={styles.reportTypeIcon} />
                <Typography variant="body1" sx={styles.reportTypeLabel}>
                  {locale.addReportTableModal.customExcelReport}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={styles.configurationArea}>
            {reportType === 'custom-excel-report' ? (
              <AddCustomExcelReport
                ref={customExcelReportRef}
                publicationId={publicationId}
                entityId={entityId}
                entitySubdomain={entitySubdomain}
                story={story}
                storyContent={storyContent}
                isTemplate={isTemplate}
                updateStoryContent={updateStoryContent}
                updateStoryContentDB={updateStoryContentDB}
                onFormDataChange={handleFormDataChange}
              />
            ) : null}

            {reportType === 'opengov-report' && entity && user ? (
              <AddOpenGovReport
                ref={openGovReportRef}
                story={story}
                storyContents={storyContents}
                entityId={entityId}
                entitySubdomain={entitySubdomain}
                entity={entity}
                user={user}
                messages={modalMessages as IModalMessages}
                isTemplate={isTemplate}
                storyContent={storyContent}
                mediaIndex={mediaIndex}
                onFormDataChange={handleFormDataChange}
                onFormLoadingChange={handleFormLoadingChange}
              />
            ) : null}

            {reportType === 'opengov-report' && (!entity || !user) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
                <CircularProgress size={32} />
              </Box>
            ) : null}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button
          onClick={onClose}
          disabled={isAdding}
          variant="outlined"
          sx={styles.actionButton}
          data-test="add-report-table-modal-cancel-button"
        >
          {locale.addReportTableModal.cancel}
        </Button>
        <Button
          onClick={handleAdd}
          disabled={!isFormValid || isAdding}
          variant="contained"
          sx={styles.actionButton}
          endIcon={isAdding || isFormLoading ? <CircularProgress size={16} sx={styles.loadingIcon} /> : null}
          data-test="add-report-table-modal-add-button"
        >
          {locale.addReportTableModal.add}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
